import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useSearchParams, Link, Navigate } from 'react-router-dom';
import Moment from 'react-moment';

import {
  fetchVideoListByToken,
  fetchMarkersBulk,
  fetchPlayerOverview,
  fetchPlayerMatchesMeta,
} from './api';
import { resultColorFor } from '../../helpers/matchResult';
import { useMappedPlayerId } from './hooks';
import {
  buildEventsFromMarkers,
  groupEventsByMatch,
  formatSeconds,
} from './matchEvents';
import MatchCard from '../../components/MatchCard';
import PlayerLoader from '../../components/PlayerLoader';
import ShareButton from '../../components/ShareButton';
import SavePlaylistButton from '../../components/SavePlaylistButton';
import FavoriteToggle from '../../components/FavoriteToggle';
import { useLexicon } from '../../hooks/useLexicon';

const CLIP_BEFORE = 10;
const CLIP_AFTER = 10;

// /player/events/video?token=… — receiver for a share-link token created by
// the Events page. Resolves the token into a marker-id list, hydrates them,
// and plays them as a clip sequence. Stays inside the router stack so the PWA
// in standalone mode doesn't pop a new browser tab.
const EventsVideo = () => {
  const l = useLexicon();
  const [searchParams] = useSearchParams();
  const mappedPlayerId = useMappedPlayerId();
  const videoRef = useRef(null);

  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [title, setTitle] = useState('');
  const [events, setEvents] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [minsByMatchId, setMinsByMatchId] = useState({});
  const [playerTeams, setPlayerTeams] = useState([]);

  // Pull matches_list once on mount so we can show MIN PLAYED in the active
  // match card. The marker resource doesn't ship mins_played, but
  // /api/v1/statistics/player_opt/{id} does — keyed by match.id.
  useEffect(() => {
    if (!mappedPlayerId) return undefined;
    let cancelled = false;
    fetchPlayerOverview(mappedPlayerId)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data?.matches_list) ? res.data.matches_list : [];
        const map = {};
        list.forEach((m) => {
          if (m?.id != null && m.mins_played != null) {
            map[String(m.id)] = Number(m.mins_played);
          }
        });
        setMinsByMatchId(map);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [mappedPlayerId]);

  // Pull player.teams from /statistics/player_matches/{id} so the active
  // match card can color its score with W/L/D logic — we need to know
  // which team the player was on to decide who won.
  useEffect(() => {
    if (!mappedPlayerId) return undefined;
    let cancelled = false;
    fetchPlayerMatchesMeta(mappedPlayerId)
      .then((res) => {
        if (cancelled) return;
        setPlayerTeams(res.data?.player?.teams ?? []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [mappedPlayerId]);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const tokenRes = await fetchVideoListByToken(token);
        if (cancelled) return;
        const data = tokenRes.data ?? {};
        if (data.title) setTitle(data.title);

        // The DB column is json_encode(input) where input was either an array
        // or a CSV string — so parsing JSON can yield either. Handle both.
        const rawMarkers = data.markers;
        let parsed = rawMarkers;
        if (typeof parsed === 'string') {
          try { parsed = JSON.parse(parsed); } catch { /* keep string */ }
        }
        if (typeof parsed === 'string') {
          parsed = parsed.split(',');
        }
        const markerIds = (Array.isArray(parsed) ? parsed : [])
          .map((n) => Number(typeof n === 'string' ? n.trim() : n))
          .filter(Number.isFinite);

        if (markerIds.length === 0) {
          setEvents([]);
          return;
        }

        const bulkRes = await fetchMarkersBulk(markerIds);
        if (cancelled) return;
        setEvents(buildEventsFromMarkers(bulkRes.data?.events ?? []));
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [token]);

  const clips = useMemo(
    () => events.map((e) => ({
      ...e,
      start: Math.max(0, e.second - CLIP_BEFORE),
      end: e.second + CLIP_AFTER,
    })),
    [events]
  );

  const matchGroups = useMemo(() => groupEventsByMatch(events), [events]);

  const activeClip = clips[activeIndex] ?? null;
  const activeKey = activeClip?.key ?? null;
  const activeVideoLink = activeClip?.videoLink ?? null;
  const activeMatch = activeClip?.match ?? null;

  // Seek to the active clip's start when it changes.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !activeClip) return;
    try {
      video.currentTime = activeClip.start;
      const playPromise = video.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    } catch {
      // Pre-metadata seek; the loadedmetadata listener will retry.
    }
  }, [activeKey]);

  // Auto-advance when reaching the end of the current clip.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !activeClip) return undefined;
    const onTime = () => {
      if (video.currentTime >= activeClip.end) {
        if (activeIndex < clips.length - 1) {
          setActiveIndex(activeIndex + 1);
        } else {
          video.pause();
        }
      }
    };
    video.addEventListener('timeupdate', onTime);
    return () => video.removeEventListener('timeupdate', onTime);
  }, [activeClip, activeIndex, clips.length]);

  // Pause when the tab is hidden. iOS Safari leaves the video element half-
  // alive in the background and throws WebKit-internal errors on restore
  // (EmptyRanges, endTimeForBufferedRangeContainingCurrentTime). Pausing on
  // visibilitychange keeps the media controller in a clean state.
  useEffect(() => {
    const onVisibility = () => {
      const video = videoRef.current;
      if (!video) return;
      if (document.hidden) {
        try { video.pause(); } catch { /* ignore */ }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  if (!mappedPlayerId) {
    return <Navigate to="/player/not-mapped" replace />;
  }

  if (!token) {
    return (
      <Wrap>
        <BackLink to="/player/events">← {l(9)}</BackLink>
        <ErrorBox>{l(1844)}</ErrorBox>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <BackLink to="/player/events">← {l(9)}</BackLink>

      {loading && <PlayerLoader label={l(1845)} />}
      {error && <ErrorBox>{l(1846)}</ErrorBox>}

      {!loading && !error && (
        <>
          {title && (
            <TitleRow>
              <Title>{title}</Title>
              {events.length > 0 && (
                <SavePlaylistButton
                  defaultTitle={title}
                  markerIds={events.map((e) => e.markerId).filter(Boolean)}
                />
              )}
            </TitleRow>
          )}

          {activeMatch && (
            <>
              {activeMatch.competition && (
                <CompetitionStrip>{activeMatch.competition}</CompetitionStrip>
              )}
              <MatchCard
                homeTeam={activeMatch.homeTeam}
                homeTeamLogo={activeMatch.homeTeamLogo}
                awayTeam={activeMatch.awayTeam}
                awayTeamLogo={activeMatch.awayTeamLogo}
                score={activeMatch.score}
                scoreColor={resultColorFor({
                  homeTeam: activeMatch.homeTeam,
                  score: activeMatch.score,
                  playerTeams,
                })}
                meta={[
                  activeMatch.date
                    ? <Moment format="DD.MM.YYYY">{activeMatch.date}</Moment>
                    : null,
                  minsByMatchId[String(activeMatch.id)] != null
                    ? `${minsByMatchId[String(activeMatch.id)]} min played`
                    : null,
                ].filter(Boolean)}
              />
            </>
          )}

          {activeVideoLink ? (
            <VideoStage>
              <StyledVideo
                ref={videoRef}
                src={activeVideoLink}
                controls
                playsInline
                preload="metadata"
              />
            </VideoStage>
          ) : (
            events.length > 0 && (
              <UnavailableStage>
                <UnavailableLabel>{l(1847)}</UnavailableLabel>
                <UnavailableHint>{l(1848)}</UnavailableHint>
              </UnavailableStage>
            )
          )}

          {activeClip && (
            <NowPlayingStrip>
              <NowPlayingTitle>
                <NowPlayingName>{activeClip.paramName}</NowPlayingName>
                {/* Star adds/removes the *currently playing* event from the
                    permanent Favorites playlist. */}
                <FavoriteToggle event={activeClip} />
              </NowPlayingTitle>
              <NowPlayingMeta>
                <NowPlayingTime>{formatSeconds(activeClip.second)}</NowPlayingTime>
                {/* Public share URL — anyone (logged-in or not) opening this
                    URL lands on /shared/playlist/:token which fetches the
                    public, no-auth /api/v2/shared/video-list/:token endpoint. */}
                <ShareButton
                  url={`${window.location.origin}/shared/playlist/${token}`}
                  title={title}
                  text={activeClip.paramName}
                />
              </NowPlayingMeta>
            </NowPlayingStrip>
          )}

          {clips.length > 0 && (
            <NavBar>
              <NavButton
                type="button"
                disabled={activeIndex <= 0}
                onClick={() => setActiveIndex((i) => Math.max(0, i - 1))}
              >
                ‹ Prev
              </NavButton>
              <Counter>{activeIndex + 1} / {clips.length}</Counter>
              <NavButton
                type="button"
                disabled={activeIndex >= clips.length - 1}
                onClick={() => setActiveIndex((i) => Math.min(clips.length - 1, i + 1))}
              >
                Next ›
              </NavButton>
            </NavBar>
          )}

          {events.length === 0 && (
            <Muted>{l(1849)}</Muted>
          )}

          {events.length > 0 && (
            <>
              <SectionHeader>
                <SectionLabel>Events · {events.length}</SectionLabel>
              </SectionHeader>

              <GroupsWrap>
                {matchGroups.map((group) => {
                  const groupHasVideo = group.events.some((e) => Boolean(e.videoLink));
                  return (
                    <Group key={group.match?.id ?? 'orphan'}>
                      {group.match && (
                        <GroupHeader>
                          <GroupTeams>
                            {group.match.homeTeam} {group.match.score ?? ''} {group.match.awayTeam}
                          </GroupTeams>
                          <GroupMeta>
                            {group.match.date && <Moment format="DD.MM.YYYY">{group.match.date}</Moment>}
                            {!groupHasVideo && <NoVideoTag>{l(1850)}</NoVideoTag>}
                          </GroupMeta>
                        </GroupHeader>
                      )}
                      <Card>
                        <ScrollList>
                          {group.events.map((e, idx) => {
                            const clipIndex = clips.findIndex((c) => c.key === e.key);
                            const isActive = activeKey === e.key;
                            return (
                              <EventRow
                                key={e.key}
                                type="button"
                                $first={idx === 0}
                                $active={isActive}
                                onClick={() => clipIndex >= 0 && setActiveIndex(clipIndex)}
                              >
                                <EventName>{e.paramName}</EventName>
                                <EventTime>{formatSeconds(e.second)}</EventTime>
                              </EventRow>
                            );
                          })}
                        </ScrollList>
                      </Card>
                    </Group>
                  );
                })}
              </GroupsWrap>
            </>
          )}
        </>
      )}
    </Wrap>
  );
};

export default EventsVideo;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px));
`;

const BackLink = styled(Link)`
  color: ${(props) => props.theme.blueish2};
  text-decoration: none;
  font-size: 13px;
  font-family: 'SatoshiMedium', sans-serif;
  align-self: flex-start;
`;

const Title = styled.h1`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 22px;
  margin: 0;
  color: ${(props) => props.theme.white};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Muted = styled.div`
  color: ${(props) => props.theme.blueish2};
  font-size: 14px;
`;

const ErrorBox = styled.div`
  color: ${(props) => props.theme.white};
  background: #3a1414;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 14px;
`;

// Competition label rendered above the shared MatchCard. Kept local
// because different surfaces have different competition-label rules
// (this page just shows the active match's competition string).
const CompetitionStrip = styled.div`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${(props) => props.theme.blueish2};
  padding: 0 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const VideoStage = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
`;

const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
`;

// Visible placeholder when the active clip has no video link — better than
// silently hiding the player and confusing the user.
const UnavailableStage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  aspect-ratio: 16 / 9;
  padding: 16px;
  background: #1a1a1a;
  border: 1px dashed ${(props) => props.theme.borderColor};
  border-radius: 12px;
  text-align: center;
  /* Without explicit box-sizing the padding + border push the layout
     wider than the parent on Chrome desktop (the global * selector
     in style/Global.js doesn't normalize box-sizing). */
  box-sizing: border-box;
`;

const UnavailableLabel = styled.div`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 13px;
  color: ${(props) => props.theme.blueish2};
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;

const UnavailableHint = styled.div`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 12px;
  color: ${(props) => props.theme.blueish2};
  max-width: 320px;
  line-height: 1.4;
`;

// Player navigation row — Prev / N / M / Next sits below the video.
// "Now playing" — ephemeral text caption, not a card. Minimal vertical
// presence; reads as a label for the video above.
const NowPlayingStrip = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 0 4px;
  margin-top: -4px;
`;

const NowPlayingName = styled.span`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 13px;
  color: ${(props) => props.theme.white};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

// Holds the event name and the star side-by-side so the star sits with the
// title rather than out on the right with the share/time meta.
const NowPlayingTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

const NowPlayingTime = styled.span`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 12px;
  letter-spacing: 0.4px;
  color: ${(props) => props.theme.green};
  flex-shrink: 0;
`;

// Holds the time pill and the share button side-by-side on the right of the
// strip so the share affordance reads as part of the now-playing context.
const NowPlayingMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const NavBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 4px 0;
`;

const NavButton = styled.button`
  border: 1px solid ${(props) => props.theme.borderColor};
  background: ${(props) => props.theme.row};
  color: ${(props) => props.theme.white};
  padding: 8px 14px;
  border-radius: 999px;
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 12px;
  cursor: pointer;
  transition: border-color 120ms ease;

  &:hover { border-color: ${(props) => props.theme.blueish2}; }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const Counter = styled.span`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 12px;
  color: ${(props) => props.theme.white};
  letter-spacing: 0.4px;
`;

// Section label outside the cards, matching the events page rhythm.
const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 4px 0;
  margin-top: 4px;
`;

const SectionLabel = styled.h3`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: ${(props) => props.theme.blueish2};
  margin: 0;
`;

const GroupsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 0 4px;
`;

const GroupTeams = styled.span`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 13px;
  color: ${(props) => props.theme.white};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const GroupMeta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 11px;
  color: ${(props) => props.theme.blueish2};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  flex-shrink: 0;

  /* color: inherit on children defeats the global * { color: theme.green }
     so the wrapped Moment span follows GroupMeta's dim blueish2 color. */
  & > * { color: inherit; }
`;

const NoVideoTag = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid ${(props) => props.theme.borderColor};
  color: ${(props) => props.theme.blueish2};
  font-size: 10px;
  letter-spacing: 0.6px;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  background: ${(props) => props.theme.row};
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: 12px;
  overflow: hidden;
`;

// Internal scroll inside each match-group card so a single-match playlist
// with 50+ events doesn't take the entire page height. ~5 rows visible at
// the typical row height (~52px including border).
//
// The global scrollbar styling in style/Global.js paints the thumb in
// theme.green — same color as our active row — so on a card scroll the
// active row and the scrollbar would visually merge into one weird
// carved shape at the rounded corner. Override locally with a slim
// neutral track + thumb.
const ScrollList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 280px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: ${(props) => props.theme.borderColor} transparent;

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 0;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${(props) => props.theme.borderColor};
    border: 0;
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: ${(props) => props.theme.blueish2};
  }
`;

const EventRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: transparent;
  color: ${(props) => props.theme.white};
  border: 0;
  border-top: 1px solid ${(props) => props.theme.borderColor};
  cursor: pointer;
  text-align: left;
  font: inherit;

  ${(props) => props.$first && `border-top: 0;`}

  ${(props) => props.$active && `
    background: ${props.theme.green};
    color: ${props.theme.black};
  `}

  &:hover {
    ${(props) => !props.$active && `background: rgba(255, 255, 255, 0.02);`}
  }
`;

// color: inherit defeats the universal "* { color: green }" rule in
// style/Global.js so the spans pick up the parent button's color
// (white when inactive, black when the row is the active clip).
const EventName = styled.span`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 14px;
  color: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const EventTime = styled.span`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 12px;
  letter-spacing: 0.3px;
  color: inherit;
  flex-shrink: 0;
  opacity: 0.85;
`;
